<template>
  <div>
    <tableComponent
      ref="tableComponent"
      :tableSettings="tableSettings"
      :tableData="tableData"
      :headers="headers"
      @openForm="openForm"
      @openMultiUserDialog="openMultiUserDialog"
      @openBulkMove="openBulkMove"
      @confirmBulkDelete="confirmBulkDelete"
    />
    <moveDialog
      :item="bulkMove.item"
      :moveDialog="bulkMove.dialog"
      :loading="bulkMove.loading"
      :array="bulkMove.targets"
      @cancel="bulkMove.dialog.open = false"
      @confirm="handleBulkMoveConfirm"
    />
    <v-dialog v-model="bulkConfirm.open" max-width="440">
      <v-card>
        <v-card-title>{{ bulkConfirm.title }}</v-card-title>
        <v-card-text>{{ bulkConfirm.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="bulkConfirm.open = false" :disabled="bulkConfirm.loading">
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="bulkConfirm.loading" @click="runBulkDelete">
            {{ $t("instructions.bulk.deleteButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <instructionForm
      ref="instructionForm"
      :data="instructionForm"
      @reloadData="reloadDataFunc"
    />
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="instruction"
      :availableItems="transferItems"
      itemText="displayName"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.instructions"
    />
  </div>
</template>
<script>
import { getUniqueId } from "../../components/api/api";
import instructionForm from "./instructionForm.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";
import moveDialog from "../../components/_utils/dataTableHelpers/actions/moveDialog.vue";

export default {
  components: {
    instructionForm,
    multiUserDialog,
    moveDialog,
  },
  data: () => ({
    tableSettings: {
      title: "",
      showSelect: true,
      showHeaderToolbar: true,
      multipleSelectButtons: [
        {
          title: "instructions.bulk.moveButton",
          color: "primary",
          action: "openBulkMove",
          icon: "content_paste_go",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "instructions.bulk.deleteButton",
          color: "error",
          action: "confirmBulkDelete",
          icon: "delete",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
      headerButtons: [
        {
          title: "multiUser.button",
          color: "primary",
          action: "openMultiUserDialog",
          icon: "mdi-swap-horizontal",
          iconDark: true,
          dark: true,
        },
        {
          title: "instructions.addInstruction",
          color: "secondary",
          action: "openForm",
          icon: "cloud_upload",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
    },
    instructionForm: {
      open: false,
      title: "instructions.formTitle",
      item: null,
      method: "new",
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
    transferItems: [],
    bulkMove: {
      item: {},
      loading: false,
      targets: [],
      ids: [],
      dialog: {
        open: false,
        title: "instructions.bulk.moveTitle",
        description: "",
      },
    },
    bulkConfirm: {
      open: false,
      loading: false,
      title: "",
      message: "",
      ids: [],
    },
  }),
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.isMultiUser = user?.role === "MultiUser";

    const res = await this.$store.dispatch("getAllInstructions");
    const instructions = (res && res.data) || res || [];
    this.transferItems = instructions.map((item) => ({
      ...item,
      displayName: item.title || item.name,
    }));

    if (!this.isMultiUser) {
      this.tableSettings.headerButtons =
        this.tableSettings.headerButtons.filter(
          (btn) => btn.action !== "openMultiUserDialog",
        );
    }
  },
  computed: {
    tableData() {
      return {
        tableComponentSlots: [
          {
            id: getUniqueId(),
            slotName: "item.name",
            componentName: "instructionName",
          },
          {
            id: getUniqueId(),
            slotName: "item.formattedDocumentType",
            componentName: "formattedDocumentType",
          },
          {
            id: getUniqueId(),
            slotName: "item.operationInstructionCreatedAt",
            componentName: "operationInstructionCreatedAt",
          },
          {
            id: getUniqueId(),
            slotName: "item.instructionAction",
            componentName: "instructionAction",
          },
          {
            id: getUniqueId(),
            slotName: "item.instructionAssignedNames",
            componentName: "instructionAssignedNames",
          },
        ],
        itemKey: "_id",
        uriPartPrepend: "instruction",
        uriPartAppend: this.$route.params.instructionFolderId
          ? `&instructionFolder=${this.$route.params.instructionFolderId}`
          : "",
        populate: [],
        sort: "-createdAt",
        editFormPushName: "instructionForm",
      };
    },
    headers() {
      return [
        {
          text: this.$t("instructions.tableHeader.description"),
          value: "name",
          align: "left",
        },
        {
          text: this.$t("instructions.tableHeader.type"),
          value: "formattedDocumentType",
        },
        {
          text: this.$t("instructions.tableHeader.createdAt"),
          value: "operationInstructionCreatedAt",
        },
        {
          text: this.$t("instructions.tableHeader.assignedNames"),
          value: "instructionAssignedNames",
        },
        {
          text: "",
          value: "instructionAction",
          align: "right",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    reloadDataFunc() {
      this.$refs.tableComponent.getData();
    },
    openForm() {
      this.instructionForm.open = true;
    },
    async openBulkMove(selectedItems) {
      this.bulkMove.ids = (selectedItems || []).map((i) => i._id);
      const res = await this.$store.dispatch("getAllInstructionFolders");
      const folders = (res && res.data) || res || [];
      const targets = [
        { id: null, name: this.$t("instructions.bulk.noFolder") },
        ...folders.map((f) => ({ id: f.id || f._id, name: f.name })),
      ];
      this.bulkMove.targets = targets.filter(
        (t) => String(t.id) !== String(this.$route.params.instructionFolderId),
      );
      this.bulkMove.dialog.description = this.$t("instructions.bulk.moveText", {
        count: this.bulkMove.ids.length,
      });
      this.bulkMove.dialog.open = true;
    },
    async handleBulkMoveConfirm(targetId) {
      try {
        this.bulkMove.loading = true;
        await this.$store.dispatch("bulkMoveInstructions", {
          ids: this.bulkMove.ids,
          instructionFolder: targetId,
        });
        this.bulkMove.dialog.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkMove.loading = false;
      }
    },
    confirmBulkDelete(selectedItems) {
      const ids = (selectedItems || []).map((i) => i._id);
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("instructions.bulk.deleteTitle"),
        message: this.$t("instructions.bulk.deleteConfirm", {
          count: ids.length,
        }),
        ids,
      };
    },
    async runBulkDelete() {
      this.bulkConfirm.loading = true;
      try {
        await this.$store.dispatch(
          "bulkDeleteInstructions",
          this.bulkConfirm.ids,
        );
        this.bulkConfirm.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkConfirm.loading = false;
      }
    },
    onBulkActionDone() {
      if (this.$refs.tableComponent) {
        this.$refs.tableComponent.clearSelectItems();
      }
      this.reloadDataFunc();
    },
    deleteItem(item) {
      this.$store
        .dispatch("deleteMultipleOperatingInstruction", item)
        .then(() => {
          this.reloadDataFunc();
          this.multipleDeleteDialog.open = false;
          this.multipleDeleteDialog.items = [];
        });
    },

    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        return;
      }
      this.multiUserOpen = true;
    },

    async executeTransfer(payload) {
      try {
        const result = await this.$store.dispatch("transferEntities", payload);

        const parts = [];
        if (result.totalSuccess > 0) {
          parts.push(
            this.$t("multiUser.result.success", {
              number: result.totalSuccess,
            }),
          );
        }
        if (result.totalConflict > 0) {
          parts.push(
            this.$t("multiUser.result.error", {
              number: result.totalConflict,
            }),
          );
        }
        if (result.totalError > 0) {
          parts.push(
            this.$t("multiUser.result.error", { number: result.totalError }),
          );
        }

        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));

        if (payload.action === "move" && result.totalSuccess > 0) {
          this.reloadDataFunc();
        }
      } catch (e) {
        console.error("Transfer failed:", e);
      }
    },
  },
};
</script>
